import {
  FiBell,
  FiBookOpen,
  FiBriefcase,
  FiCompass,
  FiCreditCard,
  FiEdit3,
  FiFileText,
  FiFlag,
  FiFolder,
  FiGlobe,
  FiGrid,
  FiHelpCircle,
  FiHome,
  FiLayers,
  FiMap,
  FiMapPin,
  FiMessageSquare,
  FiSettings,
  FiShield,
  FiSliders,
  FiTag,
  FiTrendingUp,
  FiUploadCloud,
  FiUser,
  FiUsers,
} from "react-icons/fi";

export const ADMIN_PANEL_HOME = "/admin";
export const ADMIN_USER_TYPES = ["SUPER_ADMIN", "ALL_HOME_DESIRE"];

const createItem = (item) => ({
  ...item,
  activePrefixes: item.activePrefixes ?? [item.to],
});

export const normalizePath = (path = "/") => {
  if (!path || path === "/") {
    return "/";
  }

  const normalized = path.startsWith("/") ? path : `/${path}`;
  return normalized.endsWith("/") ? normalized.slice(0, -1) : normalized;
};

export const matchesPathPrefix = (pathname, prefix) => {
  const current = normalizePath(pathname);
  const target = normalizePath(prefix);

  return current === target || current.startsWith(`${target}/`);
};

export const getAuthPayload = (authState) => authState?.user ?? authState ?? null;

export const getAuthUser = (authState) => {
  const payload = getAuthPayload(authState);

  if (!payload) {
    return null;
  }

  return payload.user ?? payload.userDetails ?? payload;
};

export const getUserTypeName = (authState) => {
  const user = getAuthUser(authState);
  return user?.userType?.name ?? user?.userType ?? null;
};

export const getUserRoles = (authState) => {
  const payload = getAuthPayload(authState);
  const user = getAuthUser(authState);

  return user?.roles ?? payload?.userDetails?.roles ?? [];
};

export const getUserDisplayName = (authState) => {
  const user = getAuthUser(authState);
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ");

  return fullName || user?.email_address || "Admin User";
};

export const isAdminUser = (authState) => {
  const typeName = getUserTypeName(authState);
  if (!typeName) return false;
  return ADMIN_USER_TYPES.some((type) =>
    type.toUpperCase() === String(typeName).toUpperCase().trim()
  );
};

export const isPartnerAdmin = (authState) =>
  getUserTypeName(authState) === "PARTNER" &&
  getUserRoles(authState).includes("ROLE_ADMIN");

export const adminSidebarSections = [
  {
    id: "overview",
    title: "Overview",
    items: [
      {
        to: "/admin",
        label: "Dashboard",
        icon: FiHome,
        activePrefixes: ["/admin"],
      },
      {
        to: "/admin/posts",
        label: "Shared Posts",
        icon: FiEdit3,
        activePrefixes: ["/admin/posts"],
      },
      {
        to: "/admin/properties",
        label: "Shared Properties",
        icon: FiBriefcase,
        activePrefixes: ["/admin/properties"],
      },
      {
        to: "/admin/promotions",
        label: "Shared Promotions",
        icon: FiTrendingUp,
        activePrefixes: ["/admin/promotions"],
      },
      {
        to: "/notification",
        label: "Notifications",
        icon: FiBell,
      },
    ],
  },
  {
    id: "people",
    title: "People",
    items: [
      {
        to: "/user",
        label: "Customers",
        icon: FiUsers,
        activePrefixes: ["/user", "/changepassword"],
      },
      {
        to: "/partners",
        label: "Partners",
        icon: FiUser,
      },
      {
        to: "/allhomedesire",
        label: "Employees",
        icon: FiShield,
      },
      {
        to: "/user-type",
        label: "User Types",
        icon: FiSettings,
      },
    ],
  },
  {
    id: "pipeline",
    title: "Pipeline",
    items: [
      {
        to: "/query",
        label: "Queries",
        icon: FiMessageSquare,
      },
      {
        to: "/contact-us",
        label: "Contact Leads",
        icon: FiHelpCircle,
      },
      {
        to: "/customer-lead",
        label: "Customer Lead",
        icon: FiUsers,
      },
      {
        to: "/lead",
        label: "Lead Management",
        icon: FiCompass,
      },
      {
        to: "/upload-leads",
        label: "Upload Leads",
        icon: FiUploadCloud,
      },
    ],
  },
  {
    id: "listings",
    title: "Listings",
    items: [
      {
        to: "/blog",
        label: "Blog",
        icon: FiBookOpen,
      },
      {
        to: "/help-center",
        label: "Help Center",
        icon: FiFolder,
      },
    ],
  },
  {
    id: "catalog",
    title: "Catalog",
    items: [
      {
        to: "/service",
        label: "Services",
        icon: FiLayers,
      },
      {
        to: "/all-services",
        label: "All Services",
        icon: FiGrid,
      },
      {
        to: "/subscription-plans",
        label: "Plans",
        icon: FiCreditCard,
      },
      {
        to: "/coupons",
        label: "Coupons",
        icon: FiTag,
      },
      {
        to: "/local-bazaar",
        label: "Local Bazaar",
        icon: FiSliders,
      },
      {
        to: "/property-purpose",
        label: "Property Purpose",
        icon: FiFlag,
      },
      {
        to: "/property-type",
        label: "Property Type",
        icon: FiTag,
      },
      {
        to: "/property-subtype",
        label: "Property Sub Type",
        icon: FiTag,
      },
      {
        to: "/property-sub-sub-type",
        label: "Property Sub Sub Type",
        icon: FiTag,
      },
      {
        to: "/property-status",
        label: "Property Status",
        icon: FiTrendingUp,
      },
      {
        to: "/property-stages",
        label: "Property Stages",
        icon: FiTrendingUp,
      },
      {
        to: "/land-types",
        label: "Land Types",
        icon: FiMap,
      },
      {
        to: "/catalog-types",
        label: "Catalog Types",
        icon: FiLayers,
      },
    ],
  },
  {
    id: "geography",
    title: "Geography",
    items: [
      {
        to: "/country",
        label: "Country",
        icon: FiGlobe,
      },
      {
        to: "/state",
        label: "State",
        icon: FiMapPin,
      },
      {
        to: "/city",
        label: "City",
        icon: FiMapPin,
      },
      {
        to: "/area",
        label: "Area",
        icon: FiMap,
      },
    ],
  },
  {
    id: "account",
    title: "Account",
    items: [
      {
        to: "/my-preference",
        label: "Preferences",
        icon: FiSettings,
      },
      {
        to: "/user/profile",
        label: "Profile",
        icon: FiUser,
        activePrefixes: ["/user/profile"],
      },
      {
        to: "/user/edit-profile",
        label: "Edit Profile",
        icon: FiEdit3,
      },
      {
        to: "/user/upload-documents",
        label: "Documents",
        icon: FiFileText,
      },
      {
        to: "/user/payment-details",
        label: "Payments",
        icon: FiCreditCard,
        activePrefixes: [
          "/user/payment-details",
          "/user/upload-payment-screen-shot",
        ],
      },
    ],
  },
].map((section) => ({
  ...section,
  items: section.items.map(createItem),
}));

export const isSidebarItemActive = (pathname, item) =>
  item.activePrefixes.some((prefix) => matchesPathPrefix(pathname, prefix));

const getActiveMatchLength = (pathname, item) =>
  item.activePrefixes.reduce((best, prefix) => {
    if (!matchesPathPrefix(pathname, prefix)) {
      return best;
    }

    return Math.max(best, normalizePath(prefix).length);
  }, -1);

export const findActiveAdminItem = (pathname) =>
  adminSidebarSections
    .flatMap((section) => section.items)
    .reduce((bestMatch, item) => {
      const matchLength = getActiveMatchLength(pathname, item);

      if (matchLength < 0) {
        return bestMatch;
      }

      if (!bestMatch || matchLength > bestMatch.matchLength) {
        return { item, matchLength };
      }

      return bestMatch;
    }, null)?.item ?? null;

export const getAdminPageTitle = (pathname) =>
  findActiveAdminItem(pathname)?.label ?? "Admin Panel";

export const ADMIN_PANEL_ROUTE_PREFIXES = Array.from(
  new Set(
    adminSidebarSections
      .flatMap((section) => section.items)
      .flatMap((item) => item.activePrefixes)
      .concat([
        "/admin",
        "/property/add-property",
        "/add-promotion",
        "/add-property",
      ])
  )
);

export const isAdminPanelRoute = (pathname, authState) =>
  isAdminUser(authState) &&
  ADMIN_PANEL_ROUTE_PREFIXES.some((prefix) =>
    matchesPathPrefix(pathname, prefix)
  );
